import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import type { ChatBookmark } from "@acto/taskpane/core/domain";

interface ChatPersistenceContextValue {
  chatHistoryId: string | null;
  customerId?: string;
  bookmarks: ChatBookmark[];
  addBookmark: (bookmark: ChatBookmark) => void;
  removeBookmark: (bookmarkId: string) => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const defaultContextValue: ChatPersistenceContextValue = {
  chatHistoryId: null,
  customerId: undefined,
  bookmarks: [],
  addBookmark: noop,
  removeBookmark: noop,
};

const ChatPersistenceContext = createContext<ChatPersistenceContextValue>(defaultContextValue);

export function useChatPersistenceContext() {
  return useContext(ChatPersistenceContext);
}

interface ChatPersistenceProviderProps {
  children: ReactNode;
  chatHistoryId: string | null;
  customerId?: string;
}

export function ChatPersistenceProvider({ children, chatHistoryId, customerId }: ChatPersistenceProviderProps) {
  const [bookmarks, setBookmarks] = useState<ChatBookmark[]>([]);

  const addBookmark = useCallback((bookmark: ChatBookmark) => {
    setBookmarks((prev) => [...prev, bookmark]);
  }, []);

  const removeBookmark = useCallback((bookmarkId: string) => {
    setBookmarks((prev) => prev.filter((b) => b.id !== bookmarkId));
  }, []);

  return (
    <ChatPersistenceContext.Provider value={{ chatHistoryId, customerId, bookmarks, addBookmark, removeBookmark }}>
      {children}
    </ChatPersistenceContext.Provider>
  );
}
